import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Select, Button, Tag, Spin, Empty, Alert, Tooltip, message, Space } from 'antd';
import { getUserDetails, makePostCall } from '../../utils/helper';
import './privilege-panel.css';

/**
 * Reporting Privilege panel (per-user × site × modality LEVEL).
 *
 * Opened from the "Privilege" button on a Users-list row. Renders a matrix
 * bounded to the sites + modalities the user is already assigned — one dropdown
 * per (modality × site) cell, level = View / Draft / Reviewed / SignOff (plus
 * No Access). It only sets the LEVEL; visibility stays owned by the user's
 * work-location + modality assignment set on the user form.
 *
 * Backend contract:
 *   POST /get-user-privileges   { username }
 *     → { sites[], modalities[], cells{ "center|modality": levelCode },
 *         level_options[], role_default_level, has_signature,
 *         enforcement_enabled, is_reporting_role }
 *   POST /save-user-privileges  { username, updated_by, cells:[{center_code,modality,level}] }
 */

interface LevelOption { value: string; label: string; ordinal: number; }
interface SiteOption { code: string; label: string; }
interface ModalityOption { code: string; label: string; fullLabel?: string; }

interface PrivilegeData {
  username: string;
  user_type: string;
  user_fullname: string;
  is_reporting_role: boolean;
  has_signature: boolean;
  /** Whether a stored signature is required to hold SignOff at all. When false, a
   *  missing signature blocks nothing and the warning is not shown. */
  signature_required: boolean;
  enforcement_enabled: boolean;
  role_default_level: string;
  level_options: LevelOption[];
  sites: SiteOption[];
  modalities: ModalityOption[];
  cells: Record<string, string>;
}

interface PrivilegePanelProps {
  visible: boolean;
  user: { username: string; user_fullname?: string; user_type?: string } | null;
  onClose: () => void;
}

const cellKey = (centerCode: string, modality: string) => `${centerCode}|${modality}`;

const PrivilegePanel: React.FC<PrivilegePanelProps> = ({ visible, user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<PrivilegeData | null>(null);
  // Working copy of the matrix (keyed "center|modality" → levelCode).
  const [cells, setCells] = useState<Record<string, string>>({});
  // Snapshot of what the server returned, to compute the delta on save.
  const [original, setOriginal] = useState<Record<string, string>>({});
  const [bulkLevel, setBulkLevel] = useState<string>('SIGNOFF');

  const fetchPrivileges = useCallback((username: string) => {
    setLoading(true);
    makePostCall('/get-user-privileges', { username })
      .then(res => {
        const payload: PrivilegeData = res.data?.data;
        setData(payload);
        setCells({ ...(payload?.cells || {}) });
        setOriginal({ ...(payload?.cells || {}) });
      })
      .catch(e => {
        message.error(e.response?.data?.message || 'Failed to load privileges');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (visible && user?.username) {
      fetchPrivileges(user.username);
    } else if (!visible) {
      setData(null);
      setCells({});
      setOriginal({});
    }
  }, [visible, user?.username, fetchPrivileges]);

  // SignOff can't be granted to a user with no signature (backend rejects it too).
  const levelOptions = useMemo(() => data?.level_options || [], [data]);
  const optionsFor = useCallback(
    (currentValue: string) =>
      levelOptions.map(opt => {
        const blockedSignoff =
          opt.value === 'SIGNOFF' && data && data.signature_required && !data.has_signature;
        return {
          value: opt.value,
          label: opt.label,
          // Keep an already-set value selectable even if now blocked, so the
          // dropdown can still display it; block only NEW selection.
          disabled: !!blockedSignoff && currentValue !== 'SIGNOFF',
        };
      }),
    [levelOptions, data]
  );

  const setCell = useCallback((key: string, value: string) => {
    setCells(prev => ({ ...prev, [key]: value }));
  }, []);

  const setColumn = useCallback((centerCode: string, value: string) => {
    if (!data) return;
    setCells(prev => {
      const next = { ...prev };
      data.modalities.forEach(m => { next[cellKey(centerCode, m.code)] = value; });
      return next;
    });
  }, [data]);

  const setRow = useCallback((modality: string, value: string) => {
    if (!data) return;
    setCells(prev => {
      const next = { ...prev };
      data.sites.forEach(s => { next[cellKey(s.code, modality)] = value; });
      return next;
    });
  }, [data]);

  const setAll = useCallback((value: string) => {
    if (!data) return;
    setCells(prev => {
      const next = { ...prev };
      data.sites.forEach(s => data.modalities.forEach(m => { next[cellKey(s.code, m.code)] = value; }));
      return next;
    });
  }, [data]);

  // Cells whose level changed from what the server returned.
  const dirtyCells = useMemo(() => {
    const out: { center_code: string; modality: string; level: string }[] = [];
    Object.keys(cells).forEach(key => {
      if (cells[key] !== original[key]) {
        const [center_code, modality] = key.split('|');
        out.push({ center_code, modality, level: cells[key] });
      }
    });
    return out;
  }, [cells, original]);

  const dirty = dirtyCells.length > 0;

  const handleSave = useCallback(() => {
    if (!data || !dirty) return;
    setSaving(true);
    makePostCall('/save-user-privileges', {
      username: data.username,
      updated_by: getUserDetails()?.username,
      cells: dirtyCells,
    })
      .then(res => {
        if (res.data?.success) {
          message.success('Reporting privileges saved');
          setOriginal({ ...cells });
        } else {
          message.error(res.data?.message || 'Failed to save privileges');
        }
      })
      .catch(e => message.error(e.response?.data?.message || 'Failed to save privileges'))
      .finally(() => setSaving(false));
  }, [data, dirty, dirtyCells, cells]);

  const bulkOptions = useMemo(
    () => levelOptions.map(o => ({
      value: o.value,
      label: o.label,
      disabled: o.value === 'SIGNOFF' && !!data && data.signature_required && !data.has_signature,
    })),
    [levelOptions, data]
  );

  const renderBody = () => {
    if (loading) {
      return <div className="priv-panel__center"><Spin tip="Loading privileges…" /></div>;
    }
    if (!data) {
      return <div className="priv-panel__center"><Empty description="No data" /></div>;
    }
    if (!data.is_reporting_role) {
      return (
        <Alert
          type="info"
          showIcon
          message="Not a reporting role"
          description="Reporting privileges apply only to radiologist, typist, consultant and HOD users."
        />
      );
    }
    if (data.sites.length === 0 || data.modalities.length === 0) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Nothing to configure"
          description="This user has no assigned sites and/or modalities. Assign them on the user form first, then set reporting levels here."
        />
      );
    }

    return (
      <>
        {/* Status banners */}
        {!data.enforcement_enabled && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Enforcement is OFF"
            description="These levels are saved but not yet enforced on report saves (REPORTING_PRIVILEGE_ENFORCED is off). Until it's enabled, users keep their current role-based access."
          />
        )}
        {data.signature_required && !data.has_signature && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="SignOff unavailable"
            description="This user has no signature on file, so SignOff can't be granted. Add a signature on the user form to enable it."
          />
        )}

        <div className="priv-panel__meta">
          <span>Default for this role: <Tag>{data.role_default_level}</Tag></span>
          <span className="priv-panel__hint">Blank cells fall back to the role default.</span>
        </div>

        {/* Bulk toolbar */}
        <div className="priv-panel__bulk">
          <span className="priv-panel__bulk-label">Set every cell to</span>
          <Select
            size="small"
            value={bulkLevel}
            style={{ width: 130 }}
            options={bulkOptions as any}
            onChange={setBulkLevel}
          />
          <Button size="small" onClick={() => setAll(bulkLevel)}>Apply to all</Button>
        </div>

        {/* Matrix: modality rows × site columns */}
        <div className="priv-panel__grid-scroll">
          <table className="priv-panel__grid">
            <thead>
              <tr>
                <th className="priv-panel__corner">Modality</th>
                {data.sites.map(site => (
                  <th key={site.code} className="priv-panel__site-head">
                    <div className="priv-panel__site-name">{site.label}</div>
                    <Select
                      size="small"
                      placeholder="set column"
                      className="priv-panel__col-set"
                      style={{ width: '100%' }}
                      value={undefined}
                      options={bulkOptions as any}
                      onChange={val => setColumn(site.code, val)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.modalities.map(mod => (
                <tr key={mod.code}>
                  <th className="priv-panel__mod-head">
                    <Tooltip title={mod.fullLabel || mod.code}>
                      <span>{mod.label}</span>
                    </Tooltip>
                    {data.sites.length > 1 && (
                      <Select
                        size="small"
                        placeholder="row"
                        className="priv-panel__row-set"
                        style={{ width: 70 }}
                        value={undefined}
                        options={bulkOptions as any}
                        onChange={val => setRow(mod.code, val)}
                      />
                    )}
                  </th>
                  {data.sites.map(site => {
                    const key = cellKey(site.code, mod.code);
                    const value = cells[key];
                    const changed = value !== original[key];
                    return (
                      <td key={key} className={`priv-panel__cell${changed ? ' priv-panel__cell--dirty' : ''}`}>
                        <Select
                          size="small"
                          style={{ width: '100%' }}
                          value={value}
                          className={`priv-level priv-level--${(value || 'NONE').toLowerCase()}`}
                          options={optionsFor(value) as any}
                          onChange={val => setCell(key, val)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <Drawer
      title={
        <div className="priv-panel__title">
          <span>Reporting Privileges</span>
          {user && (
            <span className="priv-panel__subtitle">
              {user.user_fullname || user.username}
              {user.user_type && <Tag style={{ marginLeft: 8 }}>{user.user_type}</Tag>}
            </span>
          )}
        </div>
      }
      width={Math.min(980, typeof window !== 'undefined' ? window.innerWidth - 80 : 980)}
      open={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <div className="priv-panel__footer">
          <span className="priv-panel__dirty-note">
            {dirty ? `${dirtyCells.length} unsaved change${dirtyCells.length > 1 ? 's' : ''}` : 'No unsaved changes'}
          </span>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" loading={saving} disabled={!dirty} onClick={handleSave}>
              Save
            </Button>
          </Space>
        </div>
      }
    >
      {renderBody()}
    </Drawer>
  );
};

export default PrivilegePanel;
